import { useEffect, useRef, useState } from 'react';
import ChannelConnection from '../../js/connection/ChannelConnection';

const REACTIONS = ['👍', '❤️', '😂', '😮', '👏', '🎉'];

type FloatItem = { id: number; emoji: string; x: number; alias: string };

type Props = {
    connection: ChannelConnection;
    showPicker: boolean;
    onPickerClose: () => void;
};

export default function EmojiReactionLayer({ connection, showPicker, onPickerClose }: Props) {
    const [floats, setFloats] = useState<FloatItem[]>([]);
    const idRef = useRef(0);

    function spawn(emoji: string, alias: string) {
        const id = idRef.current++;
        const x = 5 + Math.random() * 55;
        setFloats(prev => [...prev, { id, emoji, x, alias }]);
        setTimeout(() => setFloats(prev => prev.filter(f => f.id !== id)), 3500);
    }

    useEffect(() => {
        const handler = ({ emoji, alias }: { emoji: string; alias: string }) => spawn(emoji, alias);
        connection.events.addListener('onreaction', handler);
        return () => connection.events.removeListener('onreaction', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [connection.events]);

    function sendReaction(emoji: string) {
        onPickerClose();
        try {
            const alias = connection.channel.getUserAlias();
            connection.messager.sendChatMessage(JSON.stringify({ type: 'reaction', emoji, alias }));
        } catch {}
    }

    return (
        <>
            {/* Floating emoji animations */}
            <div className="rfl-layer">
                {floats.map(f => (
                    <div key={f.id} className="rfl-float" style={{ left: `${f.x}%` }}>
                        <span className="rfl-emoji">{f.emoji}</span>
                        <span className="rfl-name">{f.alias}</span>
                    </div>
                ))}
            </div>

            {/* Emoji picker popup */}
            {showPicker && (
                <>
                    <div className="rfl-backdrop" onClick={onPickerClose} />
                    <div className="rfl-picker">
                        {REACTIONS.map(e => (
                            <button key={e} className="rfl-pick-btn" title={e} onClick={() => sendReaction(e)}>
                                {e}
                            </button>
                        ))}
                    </div>
                </>
            )}

            <style>{`
                .rfl-layer {
                    position: fixed;
                    left: 0; right: 0; top: 0;
                    bottom: 96px;
                    pointer-events: none;
                    overflow: hidden;
                    z-index: 35;
                }
                .rfl-float {
                    position: absolute;
                    bottom: 0;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 4px;
                    animation: rflUp 3.5s ease-out forwards;
                }
                @keyframes rflUp {
                    0%   { opacity: 1; transform: translateY(0) scale(0.8); }
                    10%  { transform: translateY(-20px) scale(1.15); }
                    80%  { opacity: 1; }
                    100% { opacity: 0; transform: translateY(-260px) scale(1); }
                }
                .rfl-emoji {
                    font-size: 2.25rem;
                    line-height: 1;
                    filter: drop-shadow(0 2px 6px rgba(0,0,0,0.5));
                }
                .rfl-name {
                    font-size: 0.68rem;
                    color: #fff;
                    background: rgba(8, 10, 16, 0.72);
                    border: 1px solid rgba(255,255,255,0.1);
                    padding: 2px 8px;
                    border-radius: 999px;
                    white-space: nowrap;
                    max-width: 90px;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }
                .rfl-backdrop {
                    position: fixed;
                    inset: 0;
                    z-index: 46;
                }
                .rfl-picker {
                    position: fixed;
                    bottom: calc(100px + env(safe-area-inset-bottom, 0px));
                    left: 50%;
                    transform: translateX(-50%);
                    z-index: 47;
                    display: flex;
                    gap: 0.2rem;
                    background: rgba(255, 255, 255, 0.95);
                    border: 1px solid var(--hx-line);
                    border-radius: 999px;
                    padding: 0.4rem 0.55rem;
                    backdrop-filter: blur(18px);
                    -webkit-backdrop-filter: blur(18px);
                    box-shadow: var(--hx-shadow);
                    animation: rflPickerIn 0.15s ease;
                    max-width: calc(100vw - 1.5rem);
                }
                @keyframes rflPickerIn {
                    from { opacity: 0; transform: translateX(-50%) scale(0.88) translateY(6px); }
                    to   { opacity: 1; transform: translateX(-50%) scale(1) translateY(0); }
                }
                .rfl-pick-btn {
                    font-size: 1.5rem;
                    width: 44px;
                    height: 44px;
                    border: none;
                    background: transparent;
                    cursor: pointer;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: background 0.12s, transform 0.12s;
                    line-height: 1;
                }
                .rfl-pick-btn:hover {
                    background: var(--hx-blue-soft);
                    transform: scale(1.2);
                }

                @media (max-width: 768px) {
                    .rfl-layer { bottom: calc(88px + env(safe-area-inset-bottom, 0px)); }
                    .rfl-picker {
                        bottom: calc(88px + env(safe-area-inset-bottom, 0px));
                        padding: 0.3rem 0.4rem;
                        gap: 0;
                    }
                    .rfl-pick-btn {
                        width: 40px;
                        height: 40px;
                        font-size: 1.35rem;
                    }
                    .rfl-emoji { font-size: 1.85rem; }
                }
            `}</style>
        </>
    );
}
